import {useState, useRef, useEffect} from "react";
import {useReveal, delayedTrigger} from "@cdx/common";
import {animated} from "react-spring";
import {css} from "@cdx/ds";
import {toMs} from "@cdx/shared/duration";
import cdxEnv from "../env";
import {api, uuidEvent} from "../lib/api";
import {getAccountFromConfig} from "../lib/request-utils";
import {getUserRole} from "../lib/auth/roles";
import {isOpenDecks} from "../lib/app-mode";
import {INVALIDATE} from "../lib/mate/mate-shared";
import type {ModelName} from "../lib/mate/mate-types";
import {createPushFlushQueue} from "../lib/push-flush-queue";
import type {Root} from "../cdx-models/Root";

const dbObToApiOp = {insert: "create", update: "update", delete: "delete"} as const;
type DbOp = keyof typeof dbObToApiOp;

type PushMessage =
  | {type: "uuid"; payload: string}
  | {type: "lastActionId"; payload: {actionId: string | null}}
  | {
      type: "action";
      payload: {
        actionId: string;
        actionName: string;
        payload: Record<string, unknown>;
        retVal: unknown;
        spreadMs: number;
      };
    }
  | {
      type: "notification";
      payload: {type: DbOp; modelName: ModelName; payload: Record<string, unknown>};
    }
  | {
      type: "model_change";
      payload: {modelName: ModelName; type: DbOp; payload: string[] | null; id: string | null};
    };

type Status = "initial" | "active" | "failed";

const toDate = (val: unknown) => (typeof val === "string" ? new Date(val) : val);

/**
 * The api reads the org off `Origin`, which the browser sets on the handshake, so a deployed build
 * names no org here. `x-account` covers the `DEFAULT_ACCOUNT` dev and on-premise setups, where the
 * hostname carries no subdomain for the server to read.
 */
const getPushUrl = () => {
  const subdomain = getAccountFromConfig();
  return subdomain
    ? `${cdxEnv.PUSH_HOST}?x-account=${encodeURIComponent(subdomain)}`
    : cdxEnv.PUSH_HOST;
};

const createSocket = ({
  onStatusChange,
  onMessage,
}: {
  onStatusChange: (status: Status) => void;
  onMessage: (message: PushMessage) => void;
}) => {
  let socket: WebSocket | null = null;
  let nextRetryDuration = 250 + Math.random() * 1250;
  const retryTrigger = delayedTrigger();

  const handleOpen = () => {
    onStatusChange("active");
    nextRetryDuration = 250 + Math.random() * 1250;
  };

  const handleMessage = (message: MessageEvent<string>) => {
    onMessage(JSON.parse(message.data));
  };

  const handleClose = () => {
    if (socket) {
      socket.onclose = null;
      socket.onmessage = null;
      socket.close();
      socket = null;
    }
    onStatusChange("failed");
    retryTrigger.fire(connect, nextRetryDuration);
    nextRetryDuration = Math.min(nextRetryDuration * 1.5, toMs.minutes(5));
  };

  const connect = () => {
    socket = new window.WebSocket(getPushUrl());
    socket.onopen = handleOpen;
    socket.onmessage = handleMessage;
    socket.onclose = handleClose;
  };

  connect();

  return () => {
    retryTrigger.cancel();
    if (socket) {
      socket.onclose = null;
      socket.onmessage = null;
      socket.close();
    }
  };
};

/**
 * Members only: the socket carries the org's whole `model_change` stream with no per-row filter, so
 * a logged-in non-member has nothing to receive on it and the api refuses the handshake.
 */
const OuterPushUpdates = ({root}: {root: Root}) => {
  if (isOpenDecks) return null;
  const {isLoaded, role} = getUserRole(root);
  if (!isLoaded || !role) return null;
  return <PushUpdates />;
};

const PushUpdates = () => {
  const [status, setStatus] = useState<Status>("initial");
  const lastActionIdRef = useRef<string | null>(null);

  useEffect(() => {
    const queue = createPushFlushQueue({
      onReset: () => api.cache.invalidateAll(),
      isHidden: () => document.visibilityState === "hidden",
    });
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") queue.handleVisible();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleMessage = (data: PushMessage) => {
      switch (data.type) {
        case "uuid": {
          uuidEvent.emit(data.payload);
          break;
        }
        case "lastActionId": {
          const {actionId} = data.payload;
          // if server was just restarted, `actionId` is still empty, so let's ignore this case to prevent DOSing ourselves...
          if (actionId && lastActionIdRef.current && lastActionIdRef.current !== actionId) {
            // eslint-disable-next-line no-console
            console.log("outdated data: clear cache");
            lastActionIdRef.current = actionId;
            queue.reset();
          }
          break;
        }
        case "action": {
          const {actionId, actionName, payload, retVal, spreadMs} = data.payload;
          const description = api.mutationDescriptions[actionName];
          if (!description) {
            console.warn(`Don't know action '${actionName}'`);
          } else {
            const converted = description.convertDataForOptimistic
              ? description.convertDataForOptimistic(payload)
              : payload;
            queue.add(() => api.cache.invalidate(description, converted, retVal), spreadMs);
          }
          lastActionIdRef.current = actionId;
          break;
        }
        case "notification": {
          const {type, modelName, payload} = data.payload;
          const hydratedPayload: Record<string, unknown> = {
            ...payload,
            lastUpdatedAt: toDate(payload.lastUpdatedAt),
            createdAt: toDate(payload.createdAt),
          };
          if (payload.snoozeUntil) hydratedPayload.snoozeUntil = toDate(payload.snoozeUntil);
          if (payload.remindMeOn) hydratedPayload.remindMeOn = toDate(payload.remindMeOn);
          queue.add(() =>
            api.cache.invalidate({type: dbObToApiOp[type], model: modelName}, hydratedPayload, {})
          );
          break;
        }
        case "model_change": {
          const {modelName, type, payload, id} = data.payload;
          const fieldValueTuples: [string, unknown][] = (payload || []).map((field) => [
            field,
            INVALIDATE,
          ]);
          if (id) fieldValueTuples.push(["id", id]);
          queue.add(() =>
            api.cache.invalidate(
              {type: dbObToApiOp[type], model: modelName},
              Object.fromEntries(fieldValueTuples),
              {}
            )
          );
          break;
        }
        default:
          console.warn(`unknown ws message with type  "${(data as {type: string}).type}"`);
      }
    };

    const destroySocket = createSocket({onStatusChange: setStatus, onMessage: handleMessage});
    return () => {
      destroySocket();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      queue.destroy();
    };
  }, []);

  const reveal = useReveal(status === "failed");

  return reveal((props) => (
    <animated.div
      style={{opacity: props.value}}
      className={css({
        colorTheme: "active25",
        border: "1px",
        rounded: 4,
        elevation: 300,
        zIndex: 3,
        textAlign: "center",
        position: "fixed",
        bottom: "4px",
        left: "50%",
        translateX: "-50%",
        textType: "label14",
        color: "primary",
        bg: "true",
        py: "4px",
        px: "16px",
      })}
    >
      Lost connection to server.
      <br />
      Trying to reconnect...
    </animated.div>
  ));
};

export default window.WebSocket ? OuterPushUpdates : () => null;
